import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import PageHero from "@/components/PageHero";
import { resolveImageUrl } from "@/components/ImageUpload";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";

export default function Gallery() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null); // { title, photos: [{albumName, url}] }
  const [idx, setIdx] = useState(0);

  useEffect(() => { api.get("/gallery?status=published").then((r) => setItems(r.data)); }, []);

  const cats = useMemo(() => ["all", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))], [items]);
  const filtered = cat === "all" ? items : items.filter((i) => i.category === cat);

  // Flatten albums into a single navigable list of {albumName, url} pairs.
  const flattenPhotos = (it) => {
    const out = [];
    const albums = Array.isArray(it.albums) ? it.albums : [];
    if (albums.length) {
      for (const a of albums) {
        for (const url of a.images || []) {
          if (url) out.push({ albumName: a.name || "", url });
        }
      }
    }
    if (!out.length) {
      // legacy fallback
      const legacy = Array.isArray(it.media_urls) ? it.media_urls : [];
      for (const url of legacy) if (url) out.push({ albumName: "", url });
      if (!out.length && it.media_url) out.push({ albumName: "", url: it.media_url });
    }
    return out;
  };

  const open = (it) => {
    const photos = flattenPhotos(it);
    if (!photos.length) return;
    setLightbox({ title: it.title, photos, albums: (it.albums || []).filter((a) => (a.images || []).length) });
    setIdx(0);
  };
  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(() => lightbox && setIdx((i) => (i + 1) % lightbox.photos.length), [lightbox]);
  const prev = useCallback(() => lightbox && setIdx((i) => (i - 1 + lightbox.photos.length) % lightbox.photos.length), [lightbox]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, next, prev]);

  return (
    <div className="bg-[color:var(--marupiara-ivory)]">
      <PageHero image={settings.gallery_hero_image} overline="Galeria" title="Momentos da nossa editora." />
      <section className="py-8 bg-[color:var(--marupiara-cream)] border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`gallery-cat-${c}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${cat === c ? "bg-[color:var(--marupiara-obsidian)] text-[color:var(--marupiara-gold)]" : "bg-white border border-neutral-300 text-neutral-700"}`}
            >{c === "all" ? "Todas" : c}</button>
          ))}
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((i) => {
            const photos = flattenPhotos(i);
            if (!photos.length) return null;
            const cover = resolveImageUrl(i.thumbnail_url || photos[0].url);
            const albumCount = (i.albums || []).filter((a) => (a.images || []).length).length;
            return (
              <button
                key={i.id}
                onClick={() => open(i)}
                data-testid={`gallery-item-${i.id}`}
                className="group relative aspect-square overflow-hidden bg-neutral-200 block text-left"
              >
                <img src={cover} alt={i.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {photos.length > 1 && (
                  <span className="absolute top-2 right-2 bg-black/70 text-[color:var(--marupiara-gold)] text-xs px-2 py-1 flex items-center gap-1">
                    <Images size={12} /> {photos.length}
                  </span>
                )}
                {albumCount > 1 && (
                  <span className="absolute top-2 left-2 bg-[color:var(--marupiara-gold)] text-[color:var(--marupiara-obsidian)] text-[10px] uppercase tracking-wider px-2 py-0.5">
                    {albumCount} álbuns
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-sm">{i.title}</span>
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-neutral-500 py-12">Nenhuma imagem publicada nesta categoria.</div>
          )}
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="gallery-lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-[color:var(--marupiara-gold)] p-2 z-10"
          ><X size={24} /></button>
          {lightbox.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                data-testid="gallery-lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-[color:var(--marupiara-gold)] p-2 z-10"
              ><ChevronLeft size={32} /></button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="gallery-lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-[color:var(--marupiara-gold)] p-2 z-10"
              ><ChevronRight size={32} /></button>
            </>
          )}
          <div className="max-w-6xl max-h-[85vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveImageUrl(lightbox.photos[idx].url)}
              alt={lightbox.title}
              className="max-w-full max-h-[70vh] object-contain"
              data-testid="gallery-lightbox-image"
            />
            <div className="text-center text-white/80">
              <div className="font-serif text-lg text-[color:var(--marupiara-gold)]">{lightbox.title}</div>
              {lightbox.photos[idx].albumName && (
                <div className="text-sm text-white/70 mt-1" data-testid="gallery-lightbox-album">
                  {lightbox.photos[idx].albumName}
                </div>
              )}
              {lightbox.photos.length > 1 && (
                <div className="text-xs mt-1">{idx + 1} / {lightbox.photos.length}</div>
              )}
            </div>
            {lightbox.albums && lightbox.albums.length > 1 && (
              <div className="flex flex-wrap gap-2 justify-center max-w-3xl">
                {lightbox.albums.map((a) => {
                  // find first photo index of this album
                  let offset = 0;
                  for (const b of lightbox.albums) {
                    if (b.id === a.id) break;
                    offset += (b.images || []).length;
                  }
                  const active = idx >= offset && idx < offset + (a.images || []).length;
                  return (
                    <button
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); setIdx(offset); }}
                      data-testid={`gallery-lightbox-album-${a.id}`}
                      className={`text-xs uppercase tracking-wider px-3 py-1 border transition-colors ${active ? "bg-[color:var(--marupiara-gold)] text-[color:var(--marupiara-obsidian)] border-[color:var(--marupiara-gold)]" : "border-white/30 text-white/70 hover:border-[color:var(--marupiara-gold)] hover:text-[color:var(--marupiara-gold)]"}`}
                    >{a.name || "Álbum"}</button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
